<template>
    <div v-if="loading && !gc" class="d-flex align-center justify-center">
        <v-progress-circular size="20" indeterminate color="primary" width="2"></v-progress-circular>
    </div>
    <div v-else-if="error">
        <v-alert type="error">
            <span v-if="error">{{ $translate('Error loading game class') }}</span>
        </v-alert>
    </div>
    <div v-else-if="gc" ref="sc" class="scroll-container" @scroll="handleScroll"  :style="{ '--card-height': cardHeight + 'px', '--step-width': stepWidth + 'px' }" @mousedown="startDragging" @mouseup="stopDragging" @mouseleave="stopDragging" @mousemove="drag" :class="{'is-dragging': isDragging}">
        <div v-for="(step, stepIndex) in steps" :key="step.id" class="step">
            <div class="step-header">{{ step.name }}</div>
            <div class="divisions" :style="{height: heightOfDivisions + 'px'}">
                <transition-group name="slide" tag="div" class="cards-container">
                    <div v-for="(division, divisionIndex) in step.divisions" :key="division.id">
                        <div class="division-card" :style="{ top: itemTop(stepIndex, divisionIndex) + 'px'}">
                            <TeamsportPlayoffTreeDivisionCard
                                :division="divisions[division.id]"
                                :ref="`division-${division.id}`"
                                :compact="stepIndex < currentStepIndex"
                                :showTime="showTime"
                                @click="gotoDivision($event, division)"
                                class="z-clickable"
                            />
                        </div>
                    </div>
                </transition-group>
            </div>
        </div>

        <svg ref="lines" class="lines" :style="linesContainerStyle">
            <path v-for="(line, index) in lines" :key="index" :d="line.d" />
        </svg>
    </div>
</template>

<script>
import TeamsportPlayoffTreeDivisionCard from './TeamsportPlayoffTreeDivisionCard.vue'
export default {
    name: 'zoezi-teamsport-playoff-tree',

    zoezi: {
        title: 'Playoff tree',
        icon: 'mdi-tournament',
        addon: 'Cup'
    },

    props: {
        gameClassId: {
            title: 'Game class ID',
            type: Number
        },
        showGroupPlay: {
            title: 'Show group play',
            type: Boolean,
            default: false
        },
        showLinesFromGroupPlay: {
            title: 'Show lines from group play',
            type: Boolean,
            default: false
        },
        showTime: {
            title: 'Show time',
            type: Boolean,
            default: true
        }
    },

    components: {
        TeamsportPlayoffTreeDivisionCard
    },

    watch: {
        gameClassId: {
            immediate: true,
            handler() {
                this.loadData()
            }
        },
        currentStepIndex() {
            clearTimeout(this.stopUpdatingLines);
            clearInterval(this.redrawLinesInterval);
            this.redrawLinesInterval = setInterval(() => {
                this.lineRedrawNeeded++;
            }, 10);
            this.stopUpdatingLines = setTimeout(() => {
                clearInterval(this.redrawLinesInterval);
            }, 1000);
        },
        steps() {
            this.$nextTick(() => {
                this.lineRedrawNeeded++;
            });
        }
    },

    data: () => ({
        currentStepIndex: 0,
        scrollDebounceTimer: null,
        cardHeight: 118,
        stepWidth: 248 + 40,
        gc: null,
        matches: [],
        loading: false,
        error: false,
        lineRedrawNeeded: 0,
        redrawLinesInterval: null,
        stopUpdatingLines: null,
        isDragging: false,
        startX: 0,
        scrollStartX: 0,
        potentialDrag: false
    }),

    methods: {
        handleScroll(event) {
            clearTimeout(this.scrollDebounceTimer);
            this.scrollDebounceTimer = setTimeout(() => {
                const scrollLeft = event.target.scrollLeft;
                const stepWidth = this.stepWidth;

                // Calculate step based on scroll position
                const activeStepIndex = Math.ceil(scrollLeft / stepWidth);

                if (activeStepIndex !== this.currentStepIndex) {
                    this.currentStepIndex = activeStepIndex;
                }
            }, 100);
        },
        startDragging(event) {
            this.startX = event.clientX;
            this.scrollStartX = this.$refs.sc.scrollLeft;
            event.preventDefault();
            this.potentialDrag = true;
        },
        drag(event) {
            if (!this.potentialDrag && !this.isDragging) {
                return;
            }

            const currentX = event.clientX;
            const deltaX = currentX - this.startX;

            if (this.potentialDrag && Math.abs(deltaX) > 5) {
                this.isDragging = true;
                this.potentialDrag = false;
            }

            if (this.isDragging) {
                // Apply the scrolling based on the total distance moved
                this.$refs.sc.scrollLeft = this.scrollStartX - deltaX;
            }
        },
        stopDragging() {
            // Reset dragging indicators
            setTimeout(() => {
                this.isDragging = false;
            }, 0);
            this.potentialDrag = false;
        },
        itemTop(stepIndex, divisionIndex) {
            if (stepIndex < this.currentStepIndex) {
                const spaceLeft = this.heightOfDivisions - (this.cardHeight/2 * this.steps[stepIndex].divisions.length);
                const space = spaceLeft / this.steps[stepIndex].divisions.length;
                return space / 2 + divisionIndex * (this.cardHeight/2 + space);
            }
            const spaceLeft = this.heightOfDivisions - this.cardHeight * this.steps[stepIndex].divisions.length;
            const space = spaceLeft / this.steps[stepIndex].divisions.length;
            return space / 2 + divisionIndex * (this.cardHeight + space);
        },
        triggerLineRedraw() {
            this.$nextTick(() => {
                this.lineRedrawNeeded++;
            });
        },
        loadData() {
            this.error = false;
            this.loading = true;

            const promises = [
                this.$api.get('/api/teamsport/gameclass/get_for_memberpage', {id: this.gameClassId}),
                this.$api.get('/api/teamsport/gameclass/matches/get', {id: this.gameClassId})
            ];

            Promise.all(promises).then(([gc, matches]) => {
                this.gc = gc;
                this.matches = matches;
            }).catch(() => {
                this.error = true;
            }).finally(() => {
                this.loading = false;
                this.triggerLineRedraw();
            });
        },
        gotoDivision($event, division) {
            if (this.isDragging) {
                $event.preventDefault();
                $event.stopPropagation();
                return
            }

            if (division.single_match) {
                const matchPage = this.$root.siteconfig?.pages?.match;
                this.$root.followLink(matchPage, division.single_match);
            } else {
                const divisionPage = this.$root.siteconfig.pages?.division;
                this.$root.followLink(divisionPage, division.id);
            }
        },
        getRefElement(refName) {
            if (this.$refs[refName]) {
                if (Array.isArray(this.$refs[refName])) {
                    return this.$refs[refName][0].$el;
                }
                return this.$refs[refName].$el;
            }
        }
    },

    computed: {
        heightOfDivisions() {
            return this.cardHeight * this.steps[this.currentStepIndex].divisions.length;
        },
        steps() {
            if (this.showGroupPlay) {
                return this.gc?.steps || [];
            }

            return this.gc?.steps.filter(step => step.transition_rules.length > 0) || [];
        },
        divisions() {
            const divisions = {};
            this.steps.forEach(step => {
                step.divisions.forEach(division => {
                    divisions[division.id] = Object.assign({}, division, {
                        matches: this.matches.filter(m => m.division_id === division.id)
                    });
                });
            });
            return divisions;
        },
        lines() {
            // Reference this to rerender the lines
            this.lineRedrawNeeded;

            function getCumulativeOffset(element) {
                let offsetLeft = 0;
                let offsetTop = 0;

                while (element) {
                    offsetLeft += element.offsetLeft;
                    offsetTop += element.offsetTop;
                    element = element.offsetParent;
                }

                return { left: offsetLeft, top: offsetTop };
            }

            const scrollContainer = this.$refs.sc;
            if (!scrollContainer) {
                return [];
            }

            const lines = [];
            this.steps.forEach(step => {
                if (step.transition_rules.length === 0) {
                    return;
                }

                step.transition_rules.forEach(rule => {
                    const fromStep = this.gc.steps.find(s => s.id === rule.from_competitionstep_id);
                    const fromStepIsGroupPlay = fromStep.transition_rules.length === 0;
                    if (fromStepIsGroupPlay && !this.showLinesFromGroupPlay) {
                        return;
                    }
                    const stepDelta = this.steps.indexOf(step) - this.steps.indexOf(fromStep);
                    if (stepDelta > 1) {
                        return;
                    }

                    const startElem = this.getRefElement(`division-${rule.from_division_id}`);
                    const endElem = this.getRefElement(`division-${rule.division_id}`);

                    if (startElem && endElem) {
                        const scrollContainerOffset = getCumulativeOffset(scrollContainer);
                        const startElemOffset = getCumulativeOffset(startElem);
                        const endElemOffset = getCumulativeOffset(endElem);

                        // Calculate positions relative to the scroll container
                        const startX = startElemOffset.left - scrollContainerOffset.left + startElem.offsetWidth;
                        const startY = startElemOffset.top - scrollContainerOffset.top + startElem.offsetHeight / 2;
                        const endX = endElemOffset.left - scrollContainerOffset.left;
                        const endY = endElemOffset.top - scrollContainerOffset.top + endElem.offsetHeight / 2;
                        const midX = startX + (endX - startX) / 2;

                        const corner1X = startX + 40; // 40 is the padding, half way between the two cards
                        const deltaY = Math.abs(endY - startY);
                        let data;

                        const baseRadius = 10;
                        let radius = deltaY < 20 ? deltaY / 2 : baseRadius;

                        if (fromStepIsGroupPlay) {
                            data = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
                        } else if (radius < 5) {
                            data = `M ${startX} ${startY} L ${endX} ${endY}`;
                        } else if (startY < endY) {
                            data = `M ${startX} ${startY}
                                    L ${corner1X - radius} ${startY}
                                    C ${corner1X} ${startY}, ${corner1X} ${startY + radius}, ${corner1X} ${startY + radius}
                                    L ${corner1X} ${endY - radius}
                                    C ${corner1X} ${endY}, ${corner1X + radius} ${endY}, ${corner1X + radius} ${endY}
                                    L ${endX} ${endY}`;
                        } else {
                            data = `M ${startX} ${startY}
                                    L ${corner1X - radius} ${startY}
                                    C ${corner1X} ${startY}, ${corner1X} ${startY - radius}, ${corner1X} ${startY - radius}
                                    L ${corner1X} ${endY + radius}
                                    C ${corner1X} ${endY}, ${corner1X + radius} ${endY}, ${corner1X + radius} ${endY}
                                    L ${endX} ${endY}`;
                        }

                        lines.push({ d: data, name: rule.name });
                    }
                });
            });
            return lines;
        },
        linesContainerStyle() {
            return {
                height: (46 + this.heightOfDivisions) + 'px', // 46 is the header height
                width: this.steps.length * this.stepWidth + 'px'
            };
        }
    }
};
</script>

<style scoped>
.scroll-container {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    position: relative;
    cursor: grab;
}

.scroll-container.is-dragging {
    cursor: grabbing;
}

.step {
    flex: 0 0 auto;
    width: var(--step-width);
    padding-left: 20px;
    padding-right: 20px;
}

@media (max-width: 768px) {
    .step {
        scroll-snap-align: start;
    }
}

.step-header {
    font-size: 20px;
    font-weight: bold;
    margin-left: 16px;
    margin-bottom: 16px;
}
.divisions {
    position: relative;
}
.division-card {
    position: absolute;
    transition: top 0.5s ease, height 0.5s ease;
    width: 100%;
    max-height: var(--card-height);
    min-height: var(--card-height);
}
.lines {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none; /* This is required to avoid svg capturing the click events */
}

.lines path {
    stroke-width: 2;
    fill: none;
    stroke: var(--z-primary);
}
</style>
